import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  Dropdown,
  Group,
  IconCaretDown,
  IconCaretUp,
  IconThreeDotsVer,
  List,
  Typography,
  Wrapper
} from '@screentone/core';
import { v4 as uuidv4 } from 'uuid';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { ArticleItem, ExternalCollectionItem, LinkedItem } from 'data/generated/graphql';
import { useAllessehCollectionSummariesQuery } from 'hooks/useAllessehCollectionSummariesQuery';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { defaultSummaryHeadline } from 'utils/article';
import { reportError } from 'utils/errorBoundary';
import { safelyParseContent } from 'utils/temp';
import { decodeHtmlEntities } from 'utils/text';
import styles from './Collection.module.scss';
import { DEFAULT_AVAILABLE_CONTENT_SLOTS } from './constants';
import { CommonContentProps } from '../commonContentProps';
import { getLinkedItemTitle } from '../LinkedItem/LinkedItem.constants';

interface CollectionProps extends CommonEntityProps, CommonContentProps {
  data: ExternalCollectionItem;
}

export const Collection = ({
  data: collection,
  // eslint-disable-next-line unused-imports/no-unused-vars
  isDraft,
  availableContentSlots,
  parentHierarchyId,
  index,
  isHistory
}: CollectionProps) => {
  const { generateHierarchyId, modifyEntity, fromAllessehContent } = useDataModelContext();
  const { renderActions, hasRenderActions } = useContextMenuActions();
  // const { data: publicationSettingsResp } = usePublicationSettings();
  const hierarchyId = generateHierarchyId(collection, parentHierarchyId, index);
  const isExternalCollection = !/^UCS(D|P)/.test(collection.attributes.id); // external collections are those were not created in Curation
  const actionsType = isHistory ? MenuActionsKeys.HistoryExternalCollection : MenuActionsKeys.DraftExternalCollection;
  const [maybeHasNoItems, setMaybeHasNoItems] = useState(collection.contentItems.length === 0);

  const [open, setOpen] = useState(false);

  const handleClick = useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  // Effective limit used both for the fetch count and for slicing the displayed items,
  // so the number of items shown always matches the number of items requested/allowed.
  const effectiveContentSlots =
    availableContentSlots ?? (collection.attributes.limit || DEFAULT_AVAILABLE_CONTENT_SLOTS);

  const {
    data: allessehCollectionData,
    refetch: refetchCollection,
    isError,
    error
  } = useAllessehCollectionSummariesQuery(collection.attributes.id, {
    enabled: false,
    staleTime: 30_000, // cache for 30 seconds
    count: effectiveContentSlots
  });

  useEffect(() => {
    setMaybeHasNoItems(collection.contentItems.length === 0);
  }, [collection.contentItems.length]);

  useEffect(() => {
    // If collection has no items, it could be that the items are not loaded yet, so we refetch the collection
    // Skip refetch in history mode — history items are already populated and refetching causes count to flicker
    if (maybeHasNoItems && !isError && !isHistory) {
      void refetchCollection();
    }
  }, [maybeHasNoItems, isError, isHistory, refetchCollection]);

  useEffect(() => {
    if (isError) {
      reportError(`Error fetching Allesseh data for collection with id ${collection.attributes.id}:`, error as Error);
    }
  }, [collection.attributes.id, error, isError]);

  useEffect(() => {
    if (!isError && allessehCollectionData) {
      modifyEntity(hierarchyId, (entity) => {
        const externalCollectionItem = entity as ExternalCollectionItem;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!externalCollectionItem.metadata) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          externalCollectionItem.metadata = {};

          // Set createdUtc
          externalCollectionItem.metadata.createdUtc = allessehCollectionData.meta.created_utc
            ? new Date(allessehCollectionData.meta.created_utc).getTime()
            : 0;

          // Set updatedUtc
          externalCollectionItem.metadata.updatedUtc = allessehCollectionData.data.attributes.updated_datetime_utc
            ? new Date(allessehCollectionData.data.attributes.updated_datetime_utc).getTime()
            : 0;

          // Set collection type
          externalCollectionItem.metadata.collectionType =
            allessehCollectionData.meta.collection_type ?? externalCollectionItem.metadata.collectionType;

          // Set collection name
          externalCollectionItem.metadata.name =
            allessehCollectionData.meta.name ?? externalCollectionItem.metadata.name;

          // Set collection creator user
          externalCollectionItem.metadata.creatorUser = allessehCollectionData.meta.creator_user
            ? { djUserId: allessehCollectionData.meta.creator_user }
            : externalCollectionItem.metadata.creatorUser;

          // Set collection revisor user
          externalCollectionItem.metadata.revisorUser = allessehCollectionData.meta.revisor_user
            ? { djUserId: allessehCollectionData.meta.revisor_user }
            : externalCollectionItem.metadata.revisorUser;

          // Set collection publication key
          externalCollectionItem.metadata.publicationKey =
            allessehCollectionData.meta.publication_key ?? externalCollectionItem.metadata.publicationKey;

          // Set collection notes
          externalCollectionItem.metadata.notes =
            allessehCollectionData.meta.notes ?? externalCollectionItem.metadata.notes;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!externalCollectionItem.contentItems) {
          externalCollectionItem.contentItems = [];
          // Set collection content items
          externalCollectionItem.contentItems = allessehCollectionData.included.map((item) =>
            fromAllessehContent(item.attributes)
          );
        }

        if (externalCollectionItem.contentItems.length === 0 && allessehCollectionData.included.length > 0) {
          // Set collection content items if it was empty but we have items from the fetched data
          externalCollectionItem.contentItems = allessehCollectionData.included.map((item) =>
            fromAllessehContent(item.attributes)
          );
        }

        // Also check for items in data.attributes.collection (for LinkedItems)
        const collectionArray = allessehCollectionData.data.attributes.collection;
        if (
          externalCollectionItem.contentItems.length === 0 &&
          Array.isArray(collectionArray) &&
          collectionArray.length > 0
        ) {
          externalCollectionItem.contentItems = collectionArray
            .map((item) => {
              if (typeof item === 'object' && 'type' in item && item.type === 'link' && 'hosted_url' in item) {
                const linkedItem: LinkedItem = {
                  type: 'LinkedItem',
                  uuid: uuidv4(),
                  attributes: {
                    type: 'link',
                    id: (item.hosted_url as string) || (item.id as string),
                    hosted_url: item.hosted_url as string,
                    product: (item.product as string) || '',
                    title: (item.title as string) || '',
                    description: (item.description as string) || ''
                  }
                };
                return linkedItem;
              }
              return null;
            })
            .filter((item): item is LinkedItem => item !== null);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, allessehCollectionData, hierarchyId, isError]);

  useEffect(() => {
    if (availableContentSlots !== collection.attributes.limit && hierarchyId) {
      modifyEntity(hierarchyId, (entity) => {
        const externalCollectionItem = entity as ExternalCollectionItem;
        externalCollectionItem.attributes.limit = availableContentSlots ?? DEFAULT_AVAILABLE_CONTENT_SLOTS;
      });
    }
  }, [availableContentSlots, collection.attributes.limit, collection.attributes.id, modifyEntity, hierarchyId]);

  const { contentItems: allContentItems } = collection;
  const contentItems = allContentItems.slice(0, effectiveContentSlots);
  const params = useMemo(
    () => ({
      hierarchyId,
      entity: collection,
      index,
      extraProperties: { isExternalCollection }
    }),
    [collection, hierarchyId, index, isExternalCollection]
  );

  return (
    <Box data-model-hierarchy-id={hierarchyId} className={styles.cardContainer}>
      <Wrapper padding={{ all: 'sm' }} className={styles.cardHeader} data-testid="collection-list-items">
        <Typography size="md" data-testid="collection-title-name">
          {collection.metadata.name}
        </Typography>
        {hasRenderActions(actionsType, params) && (
          <Dropdown
            padding={{ all: 'none' }}
            position="right"
            trigger={<IconThreeDotsVer color="asphalt" />}
            data-testid="collection-ellipsis-icon"
          >
            {renderActions(actionsType, params)}
          </Dropdown>
        )}
      </Wrapper>
      <Divider />
      <Wrapper padding={{ all: 'sm' }} className={styles.cardHeader}>
        <Typography size="md" data-testid="query-item-count">
          {contentItems.length} {contentItems.length === 1 ? 'item' : 'items'}
        </Typography>
        <Dropdown
          open={open}
          padding={{ all: 'none' }}
          position="right"
          onToggle={handleClick}
          trigger={
            <Group gap="xs">
              <Typography variant="note" data-testid="page-query-view-items">
                View items
              </Typography>
              {open ? <IconCaretUp color="asphalt" /> : <IconCaretDown color="asphalt" />}
            </Group>
          }
        />
      </Wrapper>
      {open && (
        <Wrapper padding={{ right: 'sm', left: 'xs', bottom: 'sm' }}>
          {effectiveContentSlots <= 0 && <Typography size="sm">No items</Typography>}
          {effectiveContentSlots > 0 && contentItems.length > 0 && (
            <List listStyle="bullet" data-testid="page-query-item-list">
              {contentItems.map((item) => {
                const isLinkedItem = item.type === 'LinkedItem';
                const itemId = item.attributes.id;
                const displayText = isLinkedItem
                  ? (() => {
                      const linkedItem = item as LinkedItem;
                      return getLinkedItemTitle(linkedItem.attributes.title, linkedItem.attributes.id);
                    })()
                  : (() => {
                      const parsedContent = safelyParseContent<AllessehContent>((item as ArticleItem).content);
                      // Guard against items where the summaries response omits `attributes` on `data`
                      // (e.g. sub-collections, cross-brand content). Fall back to the item ID.
                      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                      if (!parsedContent?.data?.attributes) {
                        console.warn('Unable to parse item', parsedContent, item);
                        return `ItemError-${itemId}`;
                      }
                      return decodeHtmlEntities(defaultSummaryHeadline(parsedContent));
                    })();

                return (
                  <List.Item key={itemId} data-allesseh-id={itemId}>
                    <Typography variant="bodytext" className={styles.headline}>
                      {displayText}
                    </Typography>
                  </List.Item>
                );
              })}
            </List>
          )}
        </Wrapper>
      )}
    </Box>
  );
};
